import { ChatApi, type ChatJobResponse, type SendChatMessageRequest } from '@/domains/chat/apis';
import type {
    EventData,
} from '@/domains/chat/types/triz-events';
import type { PsHttpSSE, SSEResponse } from '@patsnap-utils/sse';
import { injectService } from '@patsnap/tahiti-core';
import { createSharedComposable, tryOnScopeDispose, useWindowFocus } from '@vueuse/core';
import { get, isNumber } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMessages } from './use-messages';
import { useMessageLog } from './use-message-log';

export const useAiAgentFlow = createSharedComposable(() => {
    const route = useRoute();
    const { te } = useI18n();
    const chatApiService = injectService<ChatApi>(ChatApi);

    // 消息列表
    const { onMessage: onMessageForMessages, reset: resetMessages } = useMessages();

    const { onMessage: onMessageForMessageLog } = useMessageLog();

    // 最后一条消息
    const lastEvent = ref<EventData | null>(null);

    // 最后一条消息 ID
    const lastEventId = computed(() => {
        if (!lastEvent.value || !('id' in lastEvent.value)) {
            return null;
        }
        return lastEvent.value.id || null;
    });

    // 心跳
    const continueTimes = ref(-1);
    const maxContinueTimes = 12000;

    // 重连
    const retryTimes = ref<number>(0);
    const maxRetryTimes = 3;
    const retryDelay = 5000;
    const cancelFlag = ref(false);
    const retryTimer = ref<any>(null);

    const WINDOW_FOCUS_CHECK_CONNECTION_TIMEOUT = 10 * 1000;

    const isGenerating = ref(false);
    const isError = ref(false);
    const currentSSE = ref<PsHttpSSE<EventData> | null>(null);
    const lastMessageTs = ref<number>(0);

    // 标记业务上是否期望SSE连接存活（不受 onError/onClose 自动重置影响）
    const expectConnected = ref(false);

    const jobInput = ref<ChatJobResponse | null>(null);

    function closeSSE() {
        console.log('前端执行closeSSE! ', new Date().toISOString())
        cancelFlag.value = true;
        expectConnected.value = false;
        retryTimer.value && clearTimeout(retryTimer.value);
        retryTimer.value = null;
        currentSSE.value?.close?.();
        currentSSE.value = null;
        isGenerating.value = false;
    }

    function newQuestion() {
        closeSSE();
        resetMessages();
        isGenerating.value = false;
        isError.value = false;
        expectConnected.value = false;
        lastEvent.value = null;
        continueTimes.value = -1;
        retryTimes.value = 0;
        retryTimer.value && clearTimeout(retryTimer.value);
        retryTimer.value = null;
        cancelFlag.value = false;
    }

    const initId = ref<number>(1);
    const hasClosed = ref(false);

    async function init(id: string, options?: { lastEventId: number | null, isRetry?: boolean }) {
        retryTimer.value && clearTimeout(retryTimer.value);

        cancelFlag.value = false;

        initId.value++
        const _initId = initId.value;

        if (_initId !== initId.value) {
            console.warn('init执行过快，此过程已被取消');
            return;
        }

        const { lastEventId: _lastEventId } = options || { lastEventId: null };

        if (!options?.isRetry) {
            isGenerating.value = true;
            isError.value = false;
        }

        expectConnected.value = true;
        hasClosed.value = false;

        currentSSE.value = chatApiService.getJobStream({
            triz_mind_id: id,
            last_id: _lastEventId as number,
            type: 'TRIZ_MIND',
        })
            .onMessage((_message: SSEResponse<EventData>) => {
                // 检查是否是当前 init 实例的消息，避免旧连接的消息干扰
                if (_initId !== initId.value) {
                    return console.log('收到旧连接的消息，已忽略', _message);
                }

                if (!currentSSE.value) {
                    return console.log('SSE is closed! ', _message);
                }

                lastMessageTs.value = Date.now();

                // 兼容后端两种格式：旧格式 { data: EventData }，新格式直接为 EventData
                const message = _message;
                // if ([SSE_EVENT_TYPE.RAG_RECALL_PROCESS].includes(message?.data?.message_type)) return;

                if ('id' in message && message.id) {
                    lastEvent.value = message;
                }

                // @ts-ignore
                continueTimes.value = isNumber(message.continue_times) ? message.continue_times : -1;

                onMessageForMessageLog(message);
                onMessageForMessages(message);

                if (retryTimes.value) {
                    console.warn('重连成功!')
                    retryTimes.value = 0;
                }
            })
            .onParseError((error: any) => {
                console.error('SSE onParseError: ', error)
            })
            .onError((error: any) => {
                const httpStatus = get(error, 'response.status');
                if (httpStatus && te(`commonStatusCode.${httpStatus}`)) {
                    return console.error('SSE onError: [没有response，不阻断操作] ', error)
                }
                console.error('SSE error:', error, error?.response)
                isError.value = true;
                isGenerating.value = false;
            })
            .onClose(() => {
                // 检查是否是当前 init 实例的关闭事件，避免旧连接触发重连
                if (_initId !== initId.value) {
                    return console.log('旧连接关闭，已忽略');
                }

                if (hasClosed.value) {
                    return;
                }

                hasClosed.value = true;
                console.log('SSE 链接关闭! ', new Date());
                isGenerating.value = false;

                if (cancelFlag.value) {
                    console.warn('前端主动取消链接，不再重连')
                    cancelFlag.value = false;
                    return;
                }

                if (continueTimes.value >= maxContinueTimes) {
                    console.warn('心跳超出阈值，不再重连')
                    expectConnected.value = false;
                    return;
                }

                if (isError.value) {
                    console.warn('捕获错误，不再重连（等待窗口焦点恢复后重试）')
                    return;
                }

                console.warn('异常断开!');
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                retry();
            })

        currentSSE.value?.start();

        return currentSSE.value;
    }

    // 窗口焦点恢复时检查连接状态（解决电脑睡眠后SSE断连不触发onClose的问题）
    watch(useWindowFocus(), (focused) => {
        console.log(focused, 'focused', 'expectConnected:', expectConnected.value, 'isGenerating:', isGenerating.value);

        if (focused && expectConnected.value) {
            const trizMindId = route.params.triz_mind_id as string;
            if (!trizMindId) return;

            // 重置错误状态，允许重新连接
            isError.value = false;
            retryTimes.value = 0;
            isGenerating.value = true;

            if (!currentSSE.value) {
                console.warn('[WindowFocus] SSE连接已丢失，重新连接');
                init(trizMindId, {
                    lastEventId: lastEventId.value as number,
                    isRetry: true,
                });
            } else if (lastMessageTs.value && Date.now() - lastMessageTs.value > WINDOW_FOCUS_CHECK_CONNECTION_TIMEOUT) {
                console.warn('[WindowFocus] 超过10s未收到消息，重新连接');
                closeSSE();
                cancelFlag.value = false;
                expectConnected.value = true;
                init(trizMindId, {
                    lastEventId: lastEventId.value as number,
                    isRetry: true,
                });
            }
        }
    });

    async function sendQuestion(data: SendChatMessageRequest) {
        const { message, reference_module, reference_version_number, reference_content } = data
        isGenerating.value = true;
        chatApiService.sendChatMessage({
            triz_mind_id: route.params.triz_mind_id as string,
            message,
            reference_module,
            reference_version_number,
            reference_content,
        }).catch((error:any) => {
            isGenerating.value = false;
            throw error
        })
    }

    async function pauseTask(triz_mind_id: string) {
        try {
            await chatApiService.interruptTask(triz_mind_id);
            // closeSSE();
        } catch (error) {
            console.error('暂停任务失败:', error);
            throw error;
        }
    }

    function retry() {
        if (retryTimes.value > maxRetryTimes) {
            expectConnected.value = false;
            return console.warn('取消重连，重连次数超限');
        }
        retryTimer.value && clearTimeout(retryTimer.value);
        retryTimer.value = setTimeout(() => {
            retryTimes.value++;
            console.warn('正在重连: ', retryTimes.value);
            init(route.params.triz_mind_id as string, {
                lastEventId: lastEventId.value as number,
                isRetry: true,
            });
        }, retryDelay);
    }

    tryOnScopeDispose(() => {
        retryTimer.value && clearTimeout(retryTimer.value);
    })

    return {
        initId,
        init,
        isGenerating,
        isError,
        newQuestion,
        sendQuestion,
        pauseTask,
        jobInput,
        lastEvent,
        lastEventId,
    }
})
